import React, { useEffect, useState } from 'react';
import { Tag, Select } from 'antd';
import { makeStyles } from '@material-ui/core';
import { DownOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import ChipInput from 'material-ui-chip-input';
import { splitStringByCharacters } from 'src/helpers/splitStringByCharacters';
import { useGetContentsQuery } from 'src/reactQuery/Content/useGetContents';
import Loader from 'src/component/Loader/Loader';
import InnerHeader from '../../component/InnerHeader';
import NoxTable from '../../component/Table';
import { CStandardInput } from '../../component/Input';
import CButton from '../../component/Button';
import Modal from '../../component/Modal/Modal';
import {
  getPushNotificationsAction,
  getPushNotificationsTopicsAction,
  createPushNotificationAction
} from '../../actions/pushNotificationAction';
import Garbage from '../../asset/garbage.svg';
import Copy from '../../component/Copy';
import formatDate from '../../helpers/formatDate';

const { Option } = Select;

const useStyles = makeStyles({
  garbage: {
    cursor: 'pointer',
    left: '8px',
    position: 'absolute',
    top: '0px'
  },
  passwordWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  select: {
    marginTop: '16px'
  }
});

const PushNotifications = () => {
  const [showModal, setShowModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState('');
  const [notification, setNotification] = useState('');
  const [topic, setTopic] = useState('Topic');
  const [contentTitle, setContentTitle] = useState('Select Content');
  const [contentId, setContentId] = useState('');
  const [userIds, setUserIds] = useState([]);
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentProject = useSelector(({ project }) => project.currentProject);

  const showModalHandler = () => {
    setShowModal(true);
  };

  const showContentModalHandler = () => {
    setShowContentModal(true);
  };

  const clearInput = () => {
    setTitle('');
    setNotification('');
    setContentTitle('Select Content');
    setContentId('');
    setTopic('topic');
    setUserIds([]);
  };

  const onCancel = () => {
    setShowModal(false);
    setShowContentModal(false);
    setEdit(false);
    clearInput();
  };

  const onSave = () => {
    dispatch(
      createPushNotificationAction(title, notification, topic, userIds, {}, () => {
        dispatch(getPushNotificationsAction());
        dispatch(getPushNotificationsTopicsAction());
      })
    );
    clearInput();
    setShowModal(false);
    setShowContentModal(false);
  };

  const onContentSave = () => {
    if (!contentId) {
      return;
    }

    dispatch(
      createPushNotificationAction(
        title,
        notification,
        topic,
        userIds,
        {
          _id: contentId,
          type: 'News'
        },
        () => {
          dispatch(getPushNotificationsAction());
          dispatch(getPushNotificationsTopicsAction());
        }
      )
    );
    clearInput();
    setShowModal(false);
    setShowContentModal(false);
  };

  const mapToTable = (pushNotifications) => {
    const data = [];

    if (!pushNotifications || pushNotifications.length === 0) {
      return data;
    }

    pushNotifications.forEach(({ title, text, topic, createdAt }, index) => {
      const formattedDate = formatDate(createdAt);
      data.push({
        addedAt: formattedDate,
        key: index,
        notification: text,
        publishedAt: formattedDate,
        title,
        topic
      });
    });

    return data;
  };

  useEffect(() => {
    async function load() {
      dispatch(getPushNotificationsAction());
      dispatch(getPushNotificationsTopicsAction());
    }

    if (currentProject) {
      load();
    }
  }, [dispatch, currentProject]);

  const pushNotifications = useSelector(
    ({ pushNotifications }) => pushNotifications.pushNotifications
  );
  const pushNotificationsTopics = useSelector(
    ({ pushNotifications }) => pushNotifications.pushNotificationsTopics
  );

  const data = mapToTable(pushNotifications);

  const ChipsTextElement = ({ value = [], onChange = () => {} }) => {
    const [chips, setChips] = useState(Array.isArray(value) ? value : []);

    const handleAddChip = (chip) => {
      setChips((prev) => {
        const chipArr = splitStringByCharacters(chip);
        const updatedChips = [...prev, ...chipArr];
        onChange(updatedChips);
        return updatedChips;
      });
    };

    const handleDeleteChip = (chip) => {
      setChips(() => {
        const filteredChips = chips.filter((item) => item !== chip);
        onChange(filteredChips);
        return filteredChips;
      });
    };

    return (
      <ChipInput
        allowDuplicates
        newChipKeys={['Enter', ' ', 'Tab']}
        onAdd={(chip) => handleAddChip(chip)}
        onDelete={(chip) => handleDeleteChip(chip)}
        placeholder="Enter tags"
        style={{ width: '95%' }}
        value={chips}
      />
    );
  };

  const columns = [
    {
      dataIndex: 'title',
      key: 'title',
      render: (title) => {
        return <Copy text={title} />;
      },
      sorter: (a, b) => a.title.localeCompare(b.title),
      title: 'Title'
    },
    {
      dataIndex: 'notification',
      key: 'notification',
      render: (notification) => {
        return <Copy text={notification} />;
      },
      sorter: (a, b) => a.notification.toString().localeCompare(b.notification),
      title: 'Notification'
    },
    {
      dataIndex: 'publishedAt',
      key: 'publishedAt',
      render: (publishedAt) => {
        return <Copy text={publishedAt} />;
      },
      sorter: (a, b) => a.publishedAt.toString().localeCompare(b.publishedAt),
      title: 'Published At'
    },
    {
      dataIndex: 'topic',
      key: 'topic',
      render: (topic) => {
        return <Copy text={topic} />;
      },
      sorter: (a, b) => a.topic.toString().localeCompare(b.topic),
      title: 'Topic'
    },
    {
      key: 'edit',
      render: () => {
        return <Tag>edit</Tag>;
      }
    }
  ];

  let pushNotificationConfig = currentProject.metadata?.pushNotification;

  if (currentProject._id.toString() === '62861dcc4bcf408cfc5dc3b9') {
    pushNotificationConfig = { contentNotificationApiId: '6298c5813e053fcde561ce08' };
  }

  const { data: contentData, isLoading: isLoadingContent } = useGetContentsQuery({
    customTypeId: pushNotificationConfig?.contentNotificationApiId,
    page: 1,
    pageSize: 100000
  });

  if (isLoadingContent) {
    return <Loader />;
  }

  const actions = [
    {
      onClick: showModalHandler,
      title: 'Create New Notification',
      type: 'primary'
    },
    ...(pushNotificationConfig
      ? [
          {
            onClick: showContentModalHandler,
            title: 'Create Content Notification',
            type: 'primary'
          }
        ]
      : [])
  ];

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={pushNotifications.length.toString()} title="Push Notifications" />
      <NoxTable actions={actions} checkbox={false} columns={columns} data={data} searchBy="title" />

      <Modal
        footerActions={
          <>
            {edit && (
              <img
                alt="delete user button"
                className={classes.garbage}
                onClick={() => {
                  //   setConfirmDelete(true);
                }}
                src={Garbage}
              />
            )}
            <CButton onClick={onCancel} type="secondary" xstyle={{ height: '40px' }}>
              Cancel
            </CButton>
            <CButton onClick={onSave} type="primary" xstyle={{ height: '40px' }}>
              Add
            </CButton>
          </>
        }
        handleClose={onCancel}
        isModalOpen={showModal}
        styles={{
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            paddingBottom: '24px',
            paddingTop: '12px'
          },
          width: '400px'
        }}
        titleContent={
          <h2 className="modal-title">{edit ? 'Edit Notification' : 'new Notification'}</h2>
        }
      >
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-push-notification-title"
          id="standard-input-push-notification-title"
          label="Title"
          onChange={(e) => setTitle(e.target.value)}
          size="large"
          value={title}
          width="true"
        />
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-push-notification-text"
          id="standard-input-push-notification-text"
          label="Notification"
          onChange={(e) => setNotification(e.target.value)}
          size="large"
          value={notification}
          width="true"
        />

        {topic === 'private' ? <ChipsTextElement onChange={setUserIds} value={userIds} /> : null}

        <Select
          className={classes.select}
          dropdownRender={(menu) => <div>{menu}</div>}
          dropdownStyle={{
            border: '1px solid #CAD8FF',
            borderRadius: '0px 0px 14px 14px',
            fontFamily: 'Poppins',
            padding: '0px',
            transform: 'translateY(5px) !important',
            width: '32px',
            zIndex: '2000'
          }}
          onChange={(e) => {
            setTopic(e);
          }}
          // defaultValue="Topic"
          suffixIcon={
            <DownOutlined
              style={{
                color: 'black',
                position: 'absolute',
                right: '8px'
              }}
            />
          }
          value={topic}
        >
          {pushNotificationsTopics && Array.isArray(pushNotificationsTopics)
            ? [...pushNotificationsTopics, { name: 'Private', value: 'private' }]?.map((topic) => {
                return <Option key={topic.value}>{topic.name}</Option>;
              })
            : []}
        </Select>
      </Modal>

      <Modal
        footerActions={
          <>
            {edit && (
              <img
                alt="delete user button"
                className={classes.garbage}
                onClick={() => {
                  //   setConfirmDelete(true);
                }}
                src={Garbage}
              />
            )}
            <CButton onClick={onCancel} type="secondary" xstyle={{ height: '40px' }}>
              Cancel
            </CButton>
            <CButton onClick={onContentSave} type="primary" xstyle={{ height: '40px' }}>
              Add
            </CButton>
          </>
        }
        handleClose={onCancel}
        isModalOpen={showContentModal}
        styles={{
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            paddingBottom: '24px',
            paddingTop: '12px'
          },
          width: '400px'
        }}
        titleContent={
          <h2 className="modal-title">{edit ? 'Edit Notification' : 'New Content Notification'}</h2>
        }
      >
        <Select
          className={classes.select}
          dropdownRender={(menu) => <div>{menu}</div>}
          dropdownStyle={{
            border: '1px solid #CAD8FF',
            borderRadius: '0px 0px 14px 14px',
            fontFamily: 'Poppins',
            padding: '0px',
            transform: 'translateY(5px) !important',
            width: '32px',
            zIndex: '2000'
          }}
          onChange={(e) => {
            const content = contentData.content.filter((content) => content._id === e)[0];

            if (content) {
              setContentTitle(content?.data?.en.title);
              setTitle(content?.data?.en.title);
              setNotification(content?.data?.en.contentText);
              setContentId(content._id);
            }
          }}
          // defaultValue="Topic"
          suffixIcon={
            <DownOutlined
              style={{
                color: 'black',
                position: 'absolute',
                right: '8px'
              }}
            />
          }
          value={contentTitle}
        >
          {Array.isArray(contentData.content)
            ? contentData.content?.map((content) => {
                const data = content?.data?.en;

                return <Option key={content._id}>{data?.title}</Option>;
              })
            : []}
        </Select>
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-push-notification-title"
          id="standard-input-push-notification-title"
          label="Title"
          onChange={(e) => setTitle(e.target.value)}
          size="large"
          value={title}
          width="true"
        />
        <CStandardInput
          disableUnderline
          formclasses={{ root: classes.root }}
          htmlFor="standard-input-push-notification-text"
          id="standard-input-push-notification-text"
          label="Notification"
          onChange={(e) => setNotification(e.target.value)}
          size="large"
          value={notification}
          width="true"
        />

        {topic === 'private' ? <ChipsTextElement onChange={setUserIds} value={userIds} /> : null}
      </Modal>
    </div>
  );
};

export default PushNotifications;
